import { useCallback, useMemo, useState } from 'react';

import PropTypes from 'prop-types';
import { BsArrowRepeat, BsClock, BsDownload, BsListUl } from 'react-icons/bs';

import AddToContactListModal from '#contactListFeature/components/AddToContactListModal/AddToContactListModal';
import Button from '#globalComponents/Button/Button';
import Card from '#globalComponents/Card/Card';
import RequireCapability from '#globalComponents/RequireCapability/RequireCapability';
import ActionScopeButton from '#globalComponents/Table/actions/ActionScopeButton';
import BulkActionsBar from '#globalComponents/Table/actions/BulkActionsBar';
import FilterChipsBar from '#globalComponents/Table/FilterChipsBar';
import TableSearchInput from '#globalComponents/Table/filters/TableSearchInput';
import Table from '#globalComponents/Table/Table';
import { useCapabilityCheck } from '#globalHooks/useCapabilityCheck';
import { useScopedSelection } from '#globalHooks/useScopedSelection';
import { useUrlFilters } from '#globalHooks/useUrlFilters';
import { buildFilterChips } from '#lib/tableFilters';

import { CAMPAIGN_CSV_COLUMN_GROUPS, CAMPAIGN_CSV_DEFAULT_COLUMNS } from '../../constants/campaignCsvExportColumns';
import { EMAIL_SEND_FILTER_SCHEMA, EMAIL_SENDS_URL_NAMESPACE } from '../../constants/emailSendFilters';
import { useExportCampaignCSV } from '../../models/emailSends/mutations';
import { useCampaignStats, useCampaignSends } from '../../models/emailSends/queries';

import BlockedRecipientsHint from './BlockedRecipientsHint';
import CampaignCSVExportModal from './CampaignCSVExportModal';
import EmailPreviewModal from './EmailPreviewModal';
import { buildEmailSendColumns } from './emailSendColumns';
import EmailSendStatCards from './EmailSendStatCards';
import RetryFailedButton from './RetryFailedButton';

const PAGE_SIZE = 100;

const formatCount = (count) => count.toLocaleString('de-DE');

const EMAIL_ENTITY_LABELS = { singular: 'E-Mail', plural: 'E-Mails' };

const NoSendsYet = () => (
	<div className="text-center py-12">
		<div className="w-16 h-16 bg-bgBase-400 rounded-full flex items-center justify-center mx-auto mb-4">
			<BsClock className="w-8 h-8 text-bgBase-800" />
		</div>
		<h3 className="text-lg font-semibold mb-2">Noch keine E-Mails</h3>
		<p className="text-bgBase-800">Statistiken werden angezeigt, sobald E-Mails versendet oder geplant wurden.</p>
	</div>
);

const StatisticsTab = ({ campaignId, campaignName, campaignStatus }) => {
	const { hasCapability: hasCrm } = useCapabilityCheck('crm.access');
	const exportCsv = useExportCampaignCSV();
	const [previewEmail, setPreviewEmail] = useState(null);
	const [exportRequest, setExportRequest] = useState(null);
	const [addToListRequest, setAddToListRequest] = useState(null);

	const { filters, filtersKey, isFiltered, setFilters, removeFilters, clearFilters, page, setPage } = useUrlFilters(
		EMAIL_SEND_FILTER_SCHEMA,
		{ namespace: EMAIL_SENDS_URL_NAMESPACE },
	);

	const { data: statsData, isFetching: isFetchingStats, refetch: refetchStats } = useCampaignStats(campaignId);
	const {
		data: sendsData,
		isLoading: isLoadingSends,
		isFetching: isFetchingSends,
		refetch: refetchSends,
	} = useCampaignSends(campaignId, { filters, page, pageSize: PAGE_SIZE });
	const isFetching = isFetchingStats || isFetchingSends;

	const stats = statsData?.stats ?? {};
	const statusCounts = statsData?.statusCounts;
	const sends = useMemo(() => sendsData?.sends ?? [], [sendsData?.sends]);
	const filteredTotal = sendsData?.filteredTotal ?? 0;
	const unsubscribedLeadIds = useMemo(
		() => new Set(sendsData?.unsubscribedLeadIds ?? []),
		[sendsData?.unsubscribedLeadIds],
	);

	const { count, clear, tableSelection, toActionScope, actionCount, actionScopeLabel, entityLabel } =
		useScopedSelection(sends, {
			totalCount: filteredTotal,
			filters,
			resetKey: `${campaignId}:${filtersKey}`,
			entityLabel: { singular: 'E-Mail', plural: 'E-Mails', gender: 'f' },
		});

	const columns = useMemo(
		() =>
			buildEmailSendColumns({
				filters,
				onFilterChange: setFilters,
				statusCounts,
				unsubscribedLeadIds,
				onPreview: setPreviewEmail,
				hasCrm,
			}),
		[filters, setFilters, statusCounts, unsubscribedLeadIds, hasCrm],
	);

	const chips = useMemo(() => buildFilterChips(filters, EMAIL_SEND_FILTER_SCHEMA), [filters]);
	const handleSearchChange = useCallback((search) => setFilters({ search }), [setFilters]);

	const openExport = () => setExportRequest({ scope: toActionScope('sendIds'), count: actionCount });
	const openAddToList = () =>
		setAddToListRequest({
			scope: { table: 'emailSends', contextId: campaignId, ...toActionScope() },
			count: actionCount,
		});

	const handleRefresh = () => {
		refetchStats();
		refetchSends();
	};

	return (
		<div className="space-y-6">
			<div className="space-y-2">
				<EmailSendStatCards stats={stats} />
				<BlockedRecipientsHint campaignId={campaignId} blockedCount={statsData?.blockedCount} />
			</div>

			<Card>
				<div className="flex items-center justify-between mb-4">
					<h3 className="text-lg font-semibold">E-Mail Versand</h3>
					<div className="flex items-center gap-2">
						<RetryFailedButton
							campaignId={campaignId}
							retryableCount={(statusCounts?.failed ?? 0) + (statusCounts?.skipped ?? 0)}
							campaignStatus={campaignStatus}
						/>
						<Button onClick={handleRefresh} variant="outline" size="sm" disabled={isFetching}>
							<BsArrowRepeat className={`h-4 w-4 mr-2 ${isFetching ? 'animate-spin' : ''}`} />
							{isFetching ? 'Aktualisiere...' : 'Aktualisieren'}
						</Button>
					</div>
				</div>

				{stats.total === 0 ? (
					<NoSendsYet />
				) : (
					<>
						<TableSearchInput
							value={filters.search}
							onChange={handleSearchChange}
							placeholder="E-Mail-Adresse oder Firma suchen …"
							className="mb-3 w-full max-w-xs"
						/>
						<FilterChipsBar
							chips={chips}
							resultLabel={`${formatCount(filteredTotal)} von ${formatCount(stats.total ?? 0)} E-Mails`}
							onRemove={removeFilters}
							onClearAll={clearFilters}
						/>
						<Table
							layoutKey="emailSends"
							rows={sends}
							columns={columns}
							isLoading={isLoadingSends}
							emptyLabel={
								isFiltered ? 'Keine E-Mails entsprechen den Filtern.' : 'Noch keine E-Mails vorhanden.'
							}
							selection={tableSelection}
							actions={{
								label: actionScopeLabel,
								buttons: (
									<>
										<ActionScopeButton
											icon={<BsListUl />}
											onClick={openAddToList}
											disabled={actionCount === 0}
										>
											Zu Kontaktliste
										</ActionScopeButton>
										<RequireCapability required="leads.export">
											<ActionScopeButton
												icon={<BsDownload />}
												onClick={openExport}
												disabled={actionCount === 0}
											>
												CSV Export
											</ActionScopeButton>
										</RequireCapability>
									</>
								),
							}}
							pagination={{
								currentPage: page,
								totalPages: Math.ceil(filteredTotal / PAGE_SIZE),
								onPageChange: setPage,
								totalItems: filteredTotal,
								pageSize: PAGE_SIZE,
							}}
							bulkBar={<BulkActionsBar selectedCount={count} entityLabel={entityLabel} onClear={clear} />}
						/>
					</>
				)}
			</Card>

			<EmailPreviewModal
				isOpen={Boolean(previewEmail)}
				onClose={() => setPreviewEmail(null)}
				email={previewEmail}
			/>

			{exportRequest && (
				<CampaignCSVExportModal
					isOpen
					onClose={() => setExportRequest(null)}
					campaignName={campaignName}
					scope={exportRequest.scope}
					count={exportRequest.count}
					columnGroups={CAMPAIGN_CSV_COLUMN_GROUPS}
					defaultColumns={CAMPAIGN_CSV_DEFAULT_COLUMNS}
					entityLabels={EMAIL_ENTITY_LABELS}
					onExport={(columns) =>
						exportCsv.mutateAsync({ campaignId, options: { columns, ...exportRequest.scope } })
					}
					isExporting={exportCsv.isPending}
				/>
			)}

			{addToListRequest && (
				<AddToContactListModal
					isOpen
					onClose={() => setAddToListRequest(null)}
					tableScope={addToListRequest.scope}
					contactCount={addToListRequest.count}
					entityLabels={EMAIL_ENTITY_LABELS}
					onSuccess={clear}
				/>
			)}
		</div>
	);
};

StatisticsTab.propTypes = {
	campaignId: PropTypes.string.isRequired,
	campaignName: PropTypes.string,
	campaignStatus: PropTypes.string,
};

export default StatisticsTab;
